import { useState, useEffect, useCallback, useRef } from 'react';
import { getEntities } from '../api/entities_api';
import { APP_CONFIG } from '../constants';

const DEFAULT_SORT_BY = 'CreatedDate';
const DEFAULT_SORT_ORDER = 'desc';

const DEFAULT_PARAMS = {
  page: 1,
  pageSize: APP_CONFIG.PAGE_SIZE,
  filters: {},
  sortBy: DEFAULT_SORT_BY,
  sortOrder: DEFAULT_SORT_ORDER,
  entity: null,
};

/**
 * Hook for managing main screen state and data fetching.
 * Holds all query state in React state and calls getEntities directly.
 *
 * @returns {Object} { params, data, entities, totalCount, loading, error, updateParams, refresh }
 */
export function useMainScreenData() {
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchInProgressRef = useRef(null);
  const prevApiParamsRef = useRef(null);

  const getApiParams = useCallback((p) => {
    if (!p) return null;
    return {
      offset: (p.page - 1) * p.pageSize,
      limit: p.pageSize,
      filters: p.filters || {},
      sortBy: p.sortBy || DEFAULT_SORT_BY,
      sortOrder: p.sortOrder || DEFAULT_SORT_ORDER,
    };
  }, []);

  const fetchDataForParams = useCallback(async (p) => {
    if (!p) return;

    const apiParams = getApiParams(p);
    if (!apiParams) return;

    const requestSignature = JSON.stringify(apiParams);

    if (fetchInProgressRef.current === requestSignature) return;
    fetchInProgressRef.current = requestSignature;

    setLoading(true);
    setError('');

    try {
      const result = await getEntities(apiParams);
      setData(result);
      setError('');
    } catch (err) {
      console.error('[useMainScreenData] Data fetch failed', err);
      setError(err.message || 'Failed to get data');
    } finally {
      if (fetchInProgressRef.current === requestSignature) {
        fetchInProgressRef.current = null;
      }
      setLoading(false);
    }
  }, [getApiParams]);

  // Fetch when API-relevant params change (not on entity/additionalColumns-only changes)
  useEffect(() => {
    const apiParamsStr = JSON.stringify(getApiParams(params));
    if (prevApiParamsRef.current === apiParamsStr) return;
    prevApiParamsRef.current = apiParamsStr;
    fetchDataForParams(params);
  }, [params, getApiParams, fetchDataForParams]);

  const updateParams = useCallback((updates) => {
    setParams((prev) => ({ ...prev, ...updates }));
  }, []);

  const refresh = useCallback(() => {
    fetchInProgressRef.current = null;
    prevApiParamsRef.current = null;
    setParams((prev) => ({ ...prev }));
  }, []);

  const entities = data?.entities || [];
  const totalCount = data?.total_count || data?.total || entities.length;

  return {
    params,
    data,
    entities,
    totalCount,
    loading,
    error,
    updateParams,
    refresh,
  };
}
